import React, { PropsWithChildren } from 'react';
import { Alert, Spin } from 'antd';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetCalculationQuery } from '../apiCalculationDetail';
import { useTemplateOrCalculation } from '../../../hooks/useTemplateOrCalculation';

/**
 * Request list statistics read SkuPotential debug rows, which are imported deferred - after the
 * calculation already reports Completed. Lock the section until the debug import finishes and show
 * an error state when it crashed; partial numbers must never be displayed.
 *
 * The wait resolves on its own: apiCalculationDetail subscribes to CalculationFlagChanged and
 * invalidates this calculation's detail when a terminal flag lands, so this guard re-renders
 * without the user reloading. A calculation that uses a request list always imports the debug rows,
 * so this section is never permanently blocked by the switch being off.
 */
const RequestListStatisticsGuard: React.FC<PropsWithChildren> = ({ children }) => {
  const templateOrCalculation = useTemplateOrCalculation();
  const { data: calculationData, isFetching } = useGetCalculationQuery(
    templateOrCalculation.type === 'Calculation' ? templateOrCalculation.id : skipToken,
  );
  if (templateOrCalculation.type !== 'Calculation') {
    return children;
  }

  if (isFetching || !calculationData) {
    return <Spin spinning={true} />;
  }

  const flags = calculationData.calculationFlags;

  if (flags.importSkuPotentialDebugCrashed) {
    return (
      <Alert
        type="error"
        showIcon
        message="Request list statistics aren't available"
        description="Some of the supporting data for this calculation couldn't be loaded, so the request list statistics can't be shown. Please re-run the calculation, or contact support if the problem persists."
      />
    );
  }

  if (!flags.importSkuPotentialDebugFinished) {
    return (
      <Alert
        type="info"
        showIcon
        message="Request list statistics are not ready yet"
        description="Potential debug data is still being imported. The statistics will become available once the import finishes."
      />
    );
  }

  return children;
};

export default RequestListStatisticsGuard;
